import { num, type ComponentForm } from '../component-types.js';

/**
 * `replicate`: mirror this entity from the server to clients. Every option
 * is optional; absent ones use the engine defaults or `settings.network`
 * (see engine/docs/systems/networking-options.md).
 */
const form: ComponentForm = {
  key: 'replicate',
  label: 'Replicate (Network)',
  icon: 'globe',
  order: 95,
  defaults: {},
  fields: [
    {
      key: 'rate',
      label: 'Rate (Hz)',
      type: num(1, { min: 0.1, max: 240 }),
      hint: 'Transform updates per second while relevant. Empty = settings.network.defaultReplicateRate.',
    },
    { key: 'range', label: 'Range', type: num(1, { min: 0 }), hint: "Relevance radius around each peer's focus (0 = everywhere).", read: (v) => v.range ?? 0 },
    {
      key: 'farRate',
      label: 'Far Rate (Hz)',
      type: num(0.5, { min: 0, max: 240 }),
      hint: 'Updates per second beyond Range; 0 hides the entity from that peer until it comes back in range.',
      showIf: (v) => Number(v.range ?? 0) > 0,
      read: (v) => v.farRate ?? 0,
    },
    { key: 'visibility', label: 'Visibility', type: { kind: 'enum', options: ['all', 'owner'] }, read: (v) => v.visibility ?? 'all' },
    { key: 'fields', label: 'Public Data Keys', type: { kind: 'tags' }, hint: '`data` keys sent to everyone. Empty/absent = all keys.' },
    { key: 'privateFields', label: 'Private Data Keys', type: { kind: 'tags' }, hint: '`data` keys sent only to the owner.' },
    { key: 'interpolate', label: 'Interpolate', type: { kind: 'bool' }, read: (v) => v.interpolate ?? true },
    {
      key: 'interpolationDelay',
      label: 'Interpolation Delay (s)',
      type: num(0.01, { min: 0, max: 2 }),
      hint: 'Seconds rendered in the past. Empty = settings.network.interpolationDelay.',
      showIf: (v) => v.interpolate !== false,
    },
    {
      key: 'extrapolationLimit',
      label: 'Extrapolation Limit (s)',
      type: num(0.05, { min: 0, max: 2 }),
      hint: 'Seconds the client may continue the last velocity when updates are late (0 = hold).',
      read: (v) => v.extrapolationLimit ?? 0,
    },
    {
      key: 'clientSimulation',
      label: 'Client Simulation',
      type: { kind: 'enum', options: ['kinematic', 'none', 'full'] },
      hint: 'Physics of client copies: kinematic (follow updates, push local bodies), none (no physics components), full (simulate locally).',
      read: (v) => v.clientSimulation ?? 'kinematic',
    },
    { key: 'stripComponentsOnClient', label: 'Strip On Client', type: { kind: 'tags' }, hint: 'Component keys removed from client copies, e.g. characterController, audioSource.' },
    {
      key: 'ownerSimulates',
      label: 'Owner Simulates',
      type: { kind: 'bool' },
      hint: 'Client-authoritative movement: the owning client moves it and the server validates (ownerMaxSpeed, on_owner_move).',
      read: (v) => v.ownerSimulates ?? false,
    },
    {
      key: 'ownerMaxSpeed',
      label: 'Owner Max Speed',
      type: num(0.5, { min: 0 }),
      hint: 'Meters per second the server accepts from the owner (0 = no limit).',
      showIf: (v) => v.ownerSimulates === true,
      read: (v) => v.ownerMaxSpeed ?? 0,
    },
    {
      key: 'isPlayerFocus',
      label: 'Player Focus',
      type: { kind: 'bool' },
      hint: "This entity's position is its owner's relevance focus for Range.",
      read: (v) => v.isPlayerFocus ?? false,
    },
  ],
};
export default form;
